/**
 * [DUBAI-SOUNDS-REAL 2026-10-23, user] The five New Deal sounds are REAL recordings now. The user did not like the five cues
 * synthesized in the browser ([DUBAI 2026-10-19]: Darbuka Celebration, Oud Fanfare, Desert Brass, Gold Souk, Burj Fireworks),
 * auditioned ten real ones on a preview page (https://claude.ai/artifact/F5Z7ddhfJCPN71BF4nixwm) and picked these five:
 * "01 Boarding Call Trap, 02 Arab Nights, 05 Qanun Flourish, 06 Ibn Al-Noor, 07 Tabuk".
 *   1 Boarding Call Trap · 2 Arab Nights · 3 Qanun Flourish · 4 Ibn Al-Noor · 5 Tabuk
 * Files: public/dubai-sounds/ (7 MP3s, 1.4 MB), each cut from its source at MP3 frame boundaries (no re-encode), a little
 * longer than used. A cue is one or two parts: an optional lead-in (the A320 cabin chime, the Arabian harp run) with the
 * music coming in under its tail, the way PM Singapore's beeps lead into its beat; all under one 2-second fade, 6.8 s in all.
 * Levels were measured on the preview: every cue sits at -14 LUFS as a TV's small speakers hear it (K-weighting after a
 * 120 Hz high-pass, so a bass-heavy beat is not judged loud for bass the TV cannot play; the five synthesized cues measured
 * -16.7 .. -11.9 on the same scale), peaks 0.83-0.95, the beats that needed it through a soft limiter. Sources: CREDITS.md.
 *
 * Which one plays: a shuffled deck (deck.ts) — all five in a random order before any repeats, the same deal keeps its
 * sound (user 10-19: "just like the gif will play randomly, the sounds also").
 *
 * ⚠ AUTOPLAY. A browser plays nothing until someone has touched the page, and the board's deploy-reload resets that. The
 * TV's Chrome must be started with --autoplay-policy=no-user-gesture-required (as for the PM Singapore TV). In code:
 * any click / key on the board unlocks it, and when a cue was refused the page shows a "tap for sound" chip.
 *
 * ⚠ THE ?v= IS LOAD-BEARING (see pmSingapore/sound.ts): Cloudflare caches these paths for four hours and the server answers
 * an unknown path with index.html, so a new file must get a new SOUND_VERSION, and no /dubai-sounds/ URL may be requested on
 * the live site before the deploy that adds it has landed.
 */
import { makeDeck } from './deck';

type Part = { file: string; at: number; offset: number; len: number; fadeOut: number };
type Preset = { id: number; name: string; len: number; gainDb: number; limit: boolean; parts: Part[] };

const SOUND_VERSION = 1;
const fileUrl = (f: string): string => `/dubai-sounds/${f}?v=${SOUND_VERSION}`;

/* at = when the part starts in the cue, offset = where in its file, len = how much of it (s); gainDb = the measured level. */
export const DUBAI_SOUNDS: Preset[] = [
  { id: 1, name: 'Boarding Call Trap', len: 6.8, gainDb: 0.11, limit: true, parts: [   // Airbus A320 cabin double chime, then a trap beat
    { file: 'boarding-call-chime.mp3', at: 0, offset: 0.018, len: 1.7, fadeOut: 0.6 },
    { file: 'boarding-call-trap.mp3', at: 0.95, offset: 0.447, len: 5.85, fadeOut: 0 }] },
  { id: 2, name: 'Arab Nights', len: 6.8, gainDb: -1.5, limit: false, parts: [
    { file: 'arab-nights.mp3', at: 0, offset: 0.427, len: 6.8, fadeOut: 0 }] },
  { id: 3, name: 'Qanun Flourish', len: 6.8, gainDb: -0.92, limit: true, parts: [   // Arabian harp run, then the groove
    { file: 'qanun-flourish-harp.mp3', at: 0, offset: 0.037, len: 1.4, fadeOut: 0.5 },
    { file: 'qanun-flourish-groove.mp3', at: 0.7, offset: 0.437, len: 6.1, fadeOut: 0 }] },
  { id: 4, name: 'Ibn Al-Noor', len: 6.8, gainDb: 0.03, limit: false, parts: [
    { file: 'ibn-al-noor.mp3', at: 0, offset: 0.421, len: 6.8, fadeOut: 0 }] },
  { id: 5, name: 'Tabuk', len: 6.8, gainDb: -0.14, limit: false, parts: [
    { file: 'tabuk.mp3', at: 0, offset: 0.432, len: 6.8, fadeOut: 0 }] },
];

/* ─────────────────────────────────────────────────────────── playing ── */

/**
 * [DUBAI-FADE 2026-10-19, user] "the sound in the new deal will play for like 5-7 seconds and it will end smoothly, like the
 * sound will fade out": every cue's last 2 seconds follow an equal-power curve down to silence. (It used to be a 1.1 s
 * exponential ramp, which spends most of its drop in the first moments and is heard as a cut.) A lead-in's own tail uses
 * the same curve over its shorter time.
 */
const FADE_S = 2.0;
const FADE_CURVE = (() => {
  const n = 256; const c = new Float32Array(n);
  for (let i = 0; i < n; i += 1) c[i] = Math.cos(((i / (n - 1)) * Math.PI) / 2);
  return c;
})();
const curveDown = (g: GainNode, at: number, dur: number): void => { g.gain.setValueCurveAtTime(FADE_CURVE, at, dur); };

const MUTE_KEY = 'dubai-muted';
const soundDeck = makeDeck(DUBAI_SOUNDS.map((p) => p.id), 'dubai-sound-deck');
let live: AudioContext | null = null;
let current: GainNode | null = null;
let blocked = false;
let seq = 0;   // bumped by every stop: a cue still waiting for its files never starts after the page has moved on

function audioCtx(): AudioContext | null {
  if (live) return live;
  const Ctor: typeof AudioContext | undefined = window.AudioContext
    || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  live = new Ctor();
  return live;
}
async function running(c: AudioContext): Promise<boolean> {
  if (c.state === 'running') return true;
  // A context with no gesture behind it never resumes — don't wait for it.
  try { await Promise.race([c.resume(), new Promise((r) => window.setTimeout(r, 300))]); } catch { /* still needs a gesture */ }
  return (c.state as string) === 'running';
}

export function isMuted(): boolean {
  try { return window.localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}
export function setMuted(v: boolean): void {
  try { window.localStorage.setItem(MUTE_KEY, v ? '1' : '0'); } catch { /* this page only */ }
  if (v) stopCue();
}
/** Was the last cue refused by the browser (no gesture yet)? Drives the "tap for sound" chip. */
export const wasBlocked = (): boolean => blocked;

/** Wake the audio WITHOUT playing anything (a tap on the board while no celebration is on screen). */
export async function unlockAudio(): Promise<boolean> {
  const c = audioCtx();
  if (!c) return false;
  const ok = await running(c);
  if (ok) blocked = false;
  return ok;
}

/** Listen ONCE for any gesture and wake the audio, so the next cue plays (nothing plays now). Returns the disposer. */
export function armAudio(onUnlock?: () => void): () => void {
  const on = (): void => { void unlockAudio().then((ok) => { if (ok && onUnlock) onUnlock(); }); };
  window.addEventListener('pointerdown', on, { once: true });
  window.addEventListener('keydown', on, { once: true });
  return () => { window.removeEventListener('pointerdown', on); window.removeEventListener('keydown', on); };
}

/** The sound this announcement gets (the deck), and its name. */
export const soundFor = (id: string): { id: number; name: string } => {
  const p = DUBAI_SOUNDS.find((x) => x.id === soundDeck.forId(id)) || DUBAI_SOUNDS[0];
  return { id: p.id, name: p.name };
};

/**
 * [DUBAI-SOUND-NOW 2026-10-19, user] "whenever the new deal page comes then immediately it should play the sound": each cue
 * is RENDERED ONCE, in the background, into a ready-to-play buffer (fetching, decoding and an OfflineAudioContext need no
 * gesture), so at the moment of the page change the cue is one buffer starting. A file that did not load (network, a
 * cached HTML page instead of audio) leaves its cue silent and the next cue tries again.
 */
const rendered = new Map<number, AudioBuffer>();
let preparing: Promise<void> | null = null;
function renderCue(OAC: typeof OfflineAudioContext, sr: number, p: Preset, bufs: Map<string, AudioBuffer>): Promise<AudioBuffer> {
  const off = new OAC(2, Math.ceil(sr * (p.len + 0.2)), sr);
  const mix = off.createGain();
  const fade = off.createGain(); fade.gain.setValueAtTime(1, 0); curveDown(fade, p.len - FADE_S, FADE_S);   // [DUBAI-FADE]
  const trim = off.createGain(); trim.gain.value = Math.pow(10, p.gainDb / 20);
  mix.connect(fade); fade.connect(trim);
  let last: AudioNode = trim;
  if (p.limit) {   // only where the beat's peaks needed it; the measured levels include it
    const lim = off.createDynamicsCompressor();
    lim.threshold.value = -3; lim.knee.value = 0; lim.ratio.value = 20; lim.attack.value = 0.002; lim.release.value = 0.15;
    last.connect(lim); last = lim;
  }
  last.connect(off.destination);
  p.parts.forEach((x) => {
    const src = off.createBufferSource(); src.buffer = bufs.get(x.file) as AudioBuffer;
    const len = Math.min(x.len, p.len - x.at);
    const g = off.createGain(); g.gain.setValueAtTime(0, x.at); g.gain.linearRampToValueAtTime(1, x.at + 0.008);   // 8 ms in: a cut never clicks
    if (x.fadeOut > 0) curveDown(g, x.at + len - x.fadeOut, x.fadeOut);
    src.connect(g); g.connect(mix); src.start(x.at, x.offset, len);
  });
  return off.startRendering();
}
export function prepareSounds(): Promise<void> {
  if (preparing) return preparing;
  const OAC: typeof OfflineAudioContext | undefined = window.OfflineAudioContext
    || (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  if (!OAC) return Promise.resolve();
  const run = (async () => {
    const sr = live ? live.sampleRate : 48000;
    const dec = new OAC(2, 1, sr);
    const bufs = new Map<string, AudioBuffer>();
    const files = Array.from(new Set(DUBAI_SOUNDS.flatMap((p) => p.parts.map((x) => x.file))));
    await Promise.all(files.map(async (f) => {
      try {
        const res = await fetch(fileUrl(f), { cache: 'force-cache' });
        if (res.ok) bufs.set(f, await dec.decodeAudioData(await res.arrayBuffer()));
      } catch { /* this file stays out; its cue is silent */ }
    }));
    for (const p of DUBAI_SOUNDS) {
      if (rendered.has(p.id) || !p.parts.every((x) => bufs.has(x.file))) continue;
      try { rendered.set(p.id, await renderCue(OAC, sr, p, bufs)); } catch { /* this cue stays silent */ }
    }
    if (rendered.size < DUBAI_SOUNDS.length) preparing = null;   // something missing: the next cue tries again
  })();
  preparing = run;
  return run;
}
/** Make the audio context now (it waits suspended until a gesture — or runs at once on a TV started with the autoplay flag). */
export function primeAudio(): void { audioCtx(); }

/** Play one announcement's sound — the next in the shuffled deck for a new id. Safe to call any time: muted, unsupported
 *  and blocked all return quietly. */
export async function playCue(id: string): Promise<void> {
  // [DUBAI-SOUND-ONLY-NEW 2026-10-19, user] never in the background: a board in a hidden tab / minimised window plays nothing
  if (isMuted() || (typeof document !== 'undefined' && document.hidden)) return;
  const c = audioCtx();
  if (!c) return;
  const ok = await running(c);
  blocked = !ok;
  if (!ok || document.hidden) return;
  stopCue();
  const my = ++seq;
  const p = DUBAI_SOUNDS.find((x) => x.id === soundDeck.forId(id)) || DUBAI_SOUNDS[0];
  // normally rendered long before the first deal; in the first seconds after the board opens, wait for it (at most 4 s)
  if (!rendered.has(p.id)) await Promise.race([prepareSounds(), new Promise((r) => window.setTimeout(r, 4000))]);
  const buf = rendered.get(p.id);
  if (!buf || my !== seq || isMuted() || document.hidden) return;
  // already through its fade, level and limiter when it was rendered — straight to the speakers
  const g = c.createGain(); g.gain.value = 1;
  const src = c.createBufferSource(); src.buffer = buf;
  src.connect(g); g.connect(c.destination);
  src.start();
  current = g;
}
/** A cue cut short (the screen moved on) still fades — 0.6 s — rather than stopping dead. */
export function stopCue(): void {
  seq += 1;
  if (!current || !live) return;
  const node = current; const now = live.currentTime; const g = node.gain;
  try {
    const hold = (g as unknown as { cancelAndHoldAtTime?: (t: number) => void }).cancelAndHoldAtTime;
    if (hold) hold.call(g, now); else { g.cancelScheduledValues(0); g.setValueAtTime(g.value, now); }
    g.linearRampToValueAtTime(0, now + 0.6);
  } catch {
    try { node.disconnect(); } catch { /* already gone */ }
  }
  current = null;
}
